import theme from 'stories/utils/theme'
import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { ButtonText } from 'stories/elements/Typography/Text'
import ReportDialog from 'components/feature/Reports/ReportDialog'
import { ReportType } from 'interfaces/UserReport'
import LikeIcon from 'assets/png-preview/like.svg'
import ShareIcon from 'assets/png-preview/download.svg'
import SaveIcon from 'assets/png-preview/bookmark.svg'
import ReportIcon from 'assets/png-preview/report.svg'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import {
    AnalyticsEventKnowConversionDialogButtonClick,
    AnalyticsEventKnowConversionSave,
    AnalyticsEventKnowConversionLike,
    AnalyticsEventKnowConversionShare,
} from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const Container = styled.aside<{ isDarkTheme?: boolean; showSeoPages?: boolean; isVisible?: boolean }>`
    position: fixed;
    border-radius: 70px;
    box-shadow: 0px 2px 16px 0px #00000014, 0px 0px 2px 0px #0000001f, 0px 4px 6px 0px #00000040;
    bottom: 16px;
    left: 50.5%;
    transform: translateX(-50%) translateY(${({ isVisible }) => (isVisible ? '0' : '120px')});
    padding: 8px 40px;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease;
    opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
    height: fit-content;
    background-color: ${theme.colors.lightGrayBackground};
    width: 900px;
    z-index: ${theme.zIndex.knowSidebar};

    .content {
        display: grid;
        gap: 35px;
        p {
            cursor: pointer;
        }
    }

    svg path {
        stroke: ${theme.colors.black};
    }

    .actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 60px;
    }
`

const ActionWrapper = styled.div<{ isReport?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 8px 10px;

    .box {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    p {
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.4px;
        color: ${theme.colors.black};
    }
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    settings: {
        isMobile?: boolean
        isDarkTheme?: boolean
    }
    onTrigger: ((type?: 'like' | 'save' | 'share', referrerScreen?: string) => void) | null
}

const DarkActionBar = ({ knowSeoInfo, settings: { isMobile, isDarkTheme }, onTrigger }: Props) => {
    const [isVisible, setIsVisible] = useState(true)
    const [lastScrollY, setLastScrollY] = useState(0)
    const { know } = knowSeoInfo ?? {}

    const referrerScreen = 'knowPreviewSidebar'

    const seoPages = know.knowDocumentPages?.slice(0, 10)

    const showSeoPages = !!seoPages?.length && seoPages.length > 2 && !isMobile

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY
            const scrollThreshold = 10

            if (currentScrollY < 100) {
                setIsVisible(true)
                setLastScrollY(currentScrollY)
                return
            }

            if (Math.abs(currentScrollY - lastScrollY) > scrollThreshold) {
                if (currentScrollY > lastScrollY) {
                    setIsVisible(false)
                } else {
                    setIsVisible(true)
                }
                setLastScrollY(currentScrollY)
            }
        }

        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [lastScrollY])

    const conversionDialogButtonClick = () => {
        trackEvent(AnalyticsEventKnowConversionDialogButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })
    }

    const saveConversionHandleClick = () => {
        trackEvent(AnalyticsEventKnowConversionSave, { know, referrerScreen })
        conversionDialogButtonClick()
        onTrigger?.('save', referrerScreen)
    }

    const likeConversionHandleClick = () => {
        trackEvent(AnalyticsEventKnowConversionLike, { know, referrerScreen })
        conversionDialogButtonClick()
        onTrigger?.('like', referrerScreen)
    }

    const shareConversionHandleClick = () => {
        trackEvent(AnalyticsEventKnowConversionShare, { know, referrerScreen })
        conversionDialogButtonClick()
        onTrigger?.('share', referrerScreen)
    }

    return (
        <Container isDarkTheme={isDarkTheme} showSeoPages={showSeoPages} isVisible={isVisible}>
            <div className="actions">
                <ActionWrapper onClick={likeConversionHandleClick}>
                    <div className="box">
                        <LikeIcon />
                    </div>
                    <ButtonText fontFamily="var(--font-inter)" fontWeight={400} color={theme.colors.actionGray}>
                        {know.likes}
                    </ButtonText>
                </ActionWrapper>
                <ActionWrapper onClick={saveConversionHandleClick}>
                    <div className="box">
                        <SaveIcon />
                    </div>
                </ActionWrapper>
                <ActionWrapper onClick={shareConversionHandleClick}>
                    <div className="box">
                        <ShareIcon />
                    </div>
                </ActionWrapper>
                <ReportDialog
                    reportType={ReportType.know}
                    reportedUserUuid={know.knower.user.uuid}
                    know={know}
                    isDarkTheme
                    icon={
                        <ActionWrapper isReport>
                            <div className="box">
                                <ReportIcon />
                            </div>
                        </ActionWrapper>
                    }
                />
            </div>
        </Container>
    )
}

export default DarkActionBar
